import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Container } from '@/components/site/Section';
import { CustomerFields, DatePicker, EMPTY_CUSTOMER, FIELD, Field, TextField, validateCustomer } from '@/components/site/forms';
import { useCatalog } from '@/state/catalog';
import { apiPost } from '@/lib/api';
import { IMAGES } from '@/lib/images';
import { Picture } from '@/components/site/Picture';
import { cn } from '@/lib/utils';

const PROJECT_TYPES = ['Vei og infrastruktur', 'Molo, kai eller sjøfylling', 'Tomteopparbeidelse og fylling', 'Bygg og fundament', 'VA-grøft og kabelgrøft', 'Hage og uteområde', 'Annet'];
const DURATIONS = ['Engangslevering', '1–4 uker', '1–3 måneder', '3–12 måneder', 'Over ett år / rammeavtale'];
const DELIVERY = [
  { id: 'ukjent', label: 'Ikke avklart' },
  { id: 'baat', label: 'Båt' },
  { id: 'tippbil', label: 'Tippbil' },
  { id: 'kranbil', label: 'Big bag med kranbil' },
  { id: 'hent', label: 'Hent selv' },
];

export default function ProjectRequestPage() {
  const catalog = useCatalog();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const preselect = sp.get('produkt');

  const [form, setForm] = useState({ projectType: '', products: preselect ? [preselect] : [], estimatedQuantityTonnes: '', location: '', startDate: null, duration: '', deliveryMethod: 'ukjent', description: '' });
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    document.title = 'Prosjektforespørsel · Hardanger Rock';
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (slug) => setForm((f) => ({ ...f, products: f.products.includes(slug) ? f.products.filter((s) => s !== slug) : [...f.products, slug] }));

  const validate = () => {
    const e = { customer: validateCustomer(customer) };
    if (!form.projectType) e.projectType = 'Velg prosjekttype.';
    if (!form.location.trim() || form.location.trim().length < 2) e.location = 'Skriv inn sted eller kommune.';
    if (!form.description.trim() || form.description.trim().length < 10) e.description = 'Beskriv prosjektet med minst noen setninger.';
    if (form.estimatedQuantityTonnes !== '' && !(Number(form.estimatedQuantityTonnes) >= 0)) e.estimatedQuantityTonnes = 'Oppgi et tall i tonn.';
    setErrors(e);
    return !(Object.keys(e.customer).length || e.projectType || e.location || e.description || e.estimatedQuantityTonnes);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError(null);
    if (!validate()) {
      toast.message('Noen felt mangler', { description: 'Se over feltene markert i rødt.' });
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        projectType: form.projectType,
        products: form.products,
        estimatedQuantityTonnes: form.estimatedQuantityTonnes === '' ? null : Number(form.estimatedQuantityTonnes),
        location: form.location.trim(),
        startDate: form.startDate,
        duration: form.duration || null,
        deliveryMethod: form.deliveryMethod,
        description: form.description.trim(),
        customer: { name: customer.name.trim(), company: customer.company.trim() || null, email: customer.email.trim(), phone: customer.phone.trim(), orgNumber: customer.orgNumber.replace(/\s/g, '') || null },
      };
      const request = await apiPost('/project-requests', body);
      navigate(`/prosjekt/bekreftet/${request.id}`, { replace: true, state: { request } });
    } catch (e) {
      setServerError(e.message || 'Noe gikk galt. Prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  const quoteProducts = catalog.products.filter((p) => p.priceType === 'quote');
  const fixedProducts = catalog.products.filter((p) => p.priceType !== 'quote');

  return (
    <div data-testid="project-request-page">
      <section className="relative isolate overflow-hidden bg-[var(--hr-heading)] text-white">
        <Picture variant={IMAGES.gravemaskinerWide} sizes="100vw" priority alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: '50% 58%' }} />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(23,26,43,0.9)_0%,rgba(23,26,43,0.7)_55%,rgba(23,26,43,0.35)_100%)]" aria-hidden="true" />
        <Container className="relative py-16 sm:py-20">
          <nav aria-label="Brødsmuler" className="text-[13px] text-white/60">
            <Link to="/" className="hover:text-white">
              Forsiden
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white/90">Prosjektforespørsel</span>
          </nav>
          <h1 className="mt-4 max-w-[16ch] font-display text-[36px] font-semibold leading-[1.05] tracking-[-0.02em] text-white sm:text-[52px]" data-testid="project-title">
            Be om prosjektpris
          </h1>
          <p className="mt-4 max-w-[58ch] text-[16px] leading-7 text-white/80 sm:text-[17px]">For stein, store volum og lengre leveranser gir vi pris per prosjekt. Fortell oss hva du skal bygge, hvor og når – så kommer vi tilbake med pris og leveringsplan.</p>
        </Container>
      </section>

      <div className="bg-[var(--hr-bg)]">
        <Container className="py-12 sm:py-16">
          <form onSubmit={submit} noValidate className="grid items-start gap-8 lg:grid-cols-[1fr_360px]" data-testid="project-form">
            <div className="space-y-6">
              <section className="hr-surface p-6 sm:p-8">
                <h2 className="font-display text-[19px] font-semibold text-[var(--hr-heading)]">Om prosjektet</h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field id="project-type" label="Prosjekttype" error={errors.projectType}>
                    <Select value={form.projectType} onValueChange={set('projectType')}>
                      <SelectTrigger id="project-type" className={cn(FIELD, 'data-[state=open]:border-[var(--hr-focus)]')} aria-invalid={!!errors.projectType} data-testid="project-type-select">
                        <SelectValue placeholder="Velg type" />
                      </SelectTrigger>
                      <SelectContent className="rounded-[10px]">
                        {PROJECT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} className="py-2.5" data-testid={`project-type-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <TextField id="project-location" label="Sted / kommune" value={form.location} onChange={set('location')} error={errors.location} placeholder="F.eks. Rosendal, Kvinnherad" />
                  <Field id="project-quantity" label="Estimert mengde (tonn)" optional error={errors.estimatedQuantityTonnes} hint="Et grovt anslag holder.">
                    <Input id="project-quantity" type="number" inputMode="decimal" min={0} value={form.estimatedQuantityTonnes} onChange={(e) => set('estimatedQuantityTonnes')(e.target.value)} placeholder="F.eks. 5000" className={cn(FIELD, 'hr-tabular')} aria-invalid={!!errors.estimatedQuantityTonnes} data-testid="project-quantity" />
                  </Field>
                  <Field id="project-duration" label="Varighet" optional>
                    <Select value={form.duration} onValueChange={set('duration')}>
                      <SelectTrigger id="project-duration" className={FIELD} data-testid="project-duration-select">
                        <SelectValue placeholder="Velg varighet" />
                      </SelectTrigger>
                      <SelectContent className="rounded-[10px]">
                        {DURATIONS.map((d) => (
                          <SelectItem key={d} value={d} className="py-2.5">
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <DatePicker id="project-start" label="Ønsket oppstart" optional value={form.startDate} onChange={set('startDate')} />
                  <Field id="project-delivery" label="Leveringsmåte">
                    <Select value={form.deliveryMethod} onValueChange={set('deliveryMethod')}>
                      <SelectTrigger id="project-delivery" className={FIELD} data-testid="project-delivery-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-[10px]">
                        {DELIVERY.map((d) => (
                          <SelectItem key={d.id} value={d.id} className="py-2.5">
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-6">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[13px] font-medium text-[var(--hr-text)]">Aktuelle produkter</span>
                    <span className="text-[12px] text-[var(--hr-muted-2)]">Valgfritt – velg flere</span>
                  </div>
                  <div className="mt-2 space-y-3">
                    {[
                      ['Prosjektprisede produkter', quoteProducts],
                      ['Standardfraksjoner', fixedProducts],
                    ].map(([label, list]) =>
                      list.length ? (
                        <div key={label}>
                          <div className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">{label}</div>
                          <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
                            {list.map((p) => {
                              const on = form.products.includes(p.slug);
                              return (
                                <button key={p.slug} type="button" aria-pressed={on} onClick={() => toggleProduct(p.slug)} className={cn('inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-[13.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', on ? 'border-[var(--hr-navy)] bg-[var(--hr-navy)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:border-[rgba(31,33,48,0.35)]')} data-testid={`project-product-${p.slug}`}>
                                  {on && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                                  {p.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null,
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <TextField id="project-description" label="Beskrivelse" multiline rows={6} value={form.description} onChange={set('description')} error={errors.description} placeholder="Hva skal bygges, hvilke lag/fraksjoner, adkomst for bil eller båt, tidsplan og eventuelle krav til dokumentasjon." />
                </div>
              </section>

              <section className="hr-surface p-6 sm:p-8">
                <h2 className="font-display text-[19px] font-semibold text-[var(--hr-heading)]">Kontaktinformasjon</h2>
                <div className="mt-6">
                  <CustomerFields value={customer} onChange={setCustomer} errors={errors.customer ?? {}} prefix="project-customer" />
                </div>
              </section>
            </div>

            <aside className="hr-surface p-6 lg:sticky lg:top-24" data-testid="project-aside">
              <h2 className="font-display text-[18px] font-semibold text-[var(--hr-heading)]">Slik går det videre</h2>
              <ol className="mt-4 space-y-4">
                {[
                  ['Vi leser forespørselen', 'og tar kontakt dersom vi trenger mer informasjon om mengde eller adkomst.'],
                  ['Du får prosjektpris', 'med leveringsmåte, tidsplan og dokumentasjon for produktene.'],
                  ['Vi avtaler levering', 'og følger opp fra første lass til siste.'],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-3">
                    <span className="hr-tabular flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--hr-accent-soft)] text-[12px] font-semibold text-[var(--hr-accent-text)]">{i + 1}</span>
                    <p className="text-[14px] leading-6 text-[var(--hr-muted)]">
                      <span className="font-semibold text-[var(--hr-heading)]">{t}</span> {d}
                    </p>
                  </li>
                ))}
              </ol>
              {serverError && (
                <Alert className="mt-5 border-[var(--hr-red)]/30 bg-[var(--hr-red-bg)] text-[var(--hr-red)]" data-testid="project-error">
                  <AlertTitle className="text-[13.5px]">Kunne ikke sende forespørselen</AlertTitle>
                  <AlertDescription className="text-[13px]">{serverError}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" disabled={submitting} className="mt-6 h-12 w-full rounded-[12px] bg-[var(--site-orange)] text-[15px] font-semibold text-[var(--site-ink)] shadow-none hover:bg-[var(--site-orange-2)] disabled:opacity-60" data-testid="project-submit-button">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Sender …
                  </>
                ) : (
                  <>
                    Send forespørsel <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>
              <p className="mt-3 text-center text-[12px] text-[var(--hr-muted-2)]">Uforpliktende. Vi deler ikke opplysningene dine.</p>
            </aside>
          </form>
        </Container>
      </div>
    </div>
  );
}
